/**
 * 강화된 프로필 페이지 (신뢰도 시스템 포함)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../api/authAPI';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TrustScoreDisplay from '../components/TrustScoreDisplay';
import ReviewList from '../components/ReviewList';
import { trustScoreAPI } from '../utils/trustAPI';
import '../components/EnhancedProfilePage.css';
import {
  Edit2, Save, X,
  Lock, LogOut, Trash2, Briefcase, Wrench, MessageSquare,
  User, Settings, Link2, Award, Target,
  ClipboardList, Star
} from 'lucide-react';

interface UserProfile {
  id: number;
  email: string;
  name: string;
  nickname: string;
  role: string;
  phone?: string;
  birth_date?: string;
  gender?: string;
  profile_image?: string;
  bio?: string;
  skills?: any;
  portfolio_url?: string;
  social_links?: any;
  social_provider?: string;
  phone_verified?: boolean;
  expert_verified?: boolean;
  created_at: string;
  last_login_at?: string;
}

interface ProfilePageProps {
  userId?: number;
}

const EnhancedProfilePage: React.FC<ProfilePageProps> = ({ userId: propUserId }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const targetUserId = user?.id;
  const isOwnProfile = true;

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    name: '',
    nickname: '',
    phone: '',
    birth_date: '',
    gender: '',
    bio: '',
    portfolio_url: '',
    skills: [] as string[]
  });

  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAPI.getProfile();
      if (response.success || response.message) {
        const userData = response.data || response.user;
        setProfile(userData);
        if (isOwnProfile) {
          setEditData({
            name: userData.name || '',
            nickname: userData.nickname || '',
            phone: userData.phone || '',
            birth_date: userData.birth_date || '',
            gender: userData.gender || '',
            bio: userData.bio || '',
            portfolio_url: userData.portfolio_url || '',
            skills: userData.skills ? JSON.parse(userData.skills) : []
          });
        }
      } else {
        setError(t('profile.loadFailed'));
      }
    } catch (err: any) {
      console.error('프로필 로드 오류:', err);
      setError(err.response?.data?.message || t('common.serverError'));
    } finally {
      setLoading(false);
    }
  }, [isOwnProfile, t]);

  useEffect(() => {
    if (targetUserId) loadProfile();
  }, [targetUserId, loadProfile]);

  const handleEdit = () => {
    if (!isOwnProfile) return;
    setEditMode(true);
  };

  const handleSave = async () => {
    if (!isOwnProfile) return;
    
    try {
      setUpdating(true);
      
      const updateData = {
        ...editData,
        skills: JSON.stringify(editData.skills)
      };

      const response = await authAPI.updateProfile(updateData);
      
      if (response.success) {
        await loadProfile();
        setEditMode(false);
        alert(t('profile.updated'));
        handleRecalculateTrust();
      } else {
        alert(t('profile.updateFailed'));
      }
    } catch (err: any) {
      console.error('프로필 업데이트 오류:', err);
      alert(err.response?.data?.message || t('common.serverError'));
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    if (!profile) return;
    
    setEditData({
      name: profile.name || '',
      nickname: profile.nickname || '',
      phone: profile.phone || '',
      birth_date: profile.birth_date || '',
      gender: profile.gender || '',
      bio: profile.bio || '',
      portfolio_url: profile.portfolio_url || '',
      skills: profile.skills ? JSON.parse(profile.skills) : []
    });
    setEditMode(false);
  };

  const handlePasswordChange = async () => {
    if (!isOwnProfile) return;
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert(t('profile.pwMismatch'));
      return;
    }

    try {
      setUpdating(true);
      
      const response = await authAPI.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );
      
      if (response.success) {
        alert(t('profile.pwChanged'));
        setShowPasswordChange(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        alert(t('profile.pwChangeFailed'));
      }
    } catch (err: any) {
      console.error('비밀번호 변경 오류:', err);
      alert(err.response?.data?.message || t('common.serverError'));
    } finally {
      setUpdating(false);
    }
  };

  const handleRecalculateTrust = async () => {
    if (!targetUserId) return;
    try {
      await trustScoreAPI.recalculateScore(targetUserId);
    } catch (err: any) {
      console.error('신뢰도 재계산 오류:', err);
    }
  };

  const addSkill = (skill: string) => {
    if (skill.trim() && !editData.skills.includes(skill.trim())) {
      setEditData({
        ...editData,
        skills: [...editData.skills, skill.trim()]
      });
    }
  };

  const removeSkill = (skillToRemove: string) => {
    setEditData({
      ...editData,
      skills: editData.skills.filter(skill => skill !== skillToRemove)
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDeleteAccount = () => {
    if (window.confirm(t('profile.deleteConfirm'))) {
      navigate('/delete-account');
    }
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner"></div>
        <p>{t('profile.loading')}</p>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="profile-error">
        <h2>❌ {t('profile.notFound')}</h2>
        <p>{error}</p>
        <button onClick={() => navigate(-1)} className="btn-back">
          {t('profile.back')}
        </button>
      </div>
    );
  }

  return (
    <div className="enhanced-profile-page">
      <div className="profile-container">
        
        {/* 프로필 헤더 */}
        <div className="profile-header">
          <div className="profile-main-info">
            {profile.profile_image && (
              <img 
                src={profile.profile_image} 
                alt={t('profile.imageAlt')} 
                className="profile-avatar"
              />
            )}
            <div className="profile-basic">
              {editMode ? (
                <div className="edit-basic-info">
                  <input
                    type="text"
                    value={editData.name}
                    onChange={(e) => setEditData({...editData, name: e.target.value})}
                    placeholder={t('profile.namePlaceholder')}
                    className="edit-input"
                  />
                  <input
                    type="text"
                    value={editData.nickname}
                    onChange={(e) => setEditData({...editData, nickname: e.target.value})}
                    placeholder={t('profile.nicknamePlaceholder')}
                    className="edit-input"
                  />
                </div>
              ) : (
                <>
                  <h1 className="profile-name">
                    {profile.name}
                    {profile.expert_verified && (
                      <span className="expert-badge" title={t('profile.expertBadge')} style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, color: '#f59e0b', verticalAlign: 'middle' }}>
                        <Award size={18} />
                      </span>
                    )}
                  </h1>
                  <p className="profile-nickname">@{profile.nickname}</p>
                </>
              )}
              
              <div className="profile-meta">
                <span className="profile-role">
                  {profile.role === 'freelancer' ? <><Wrench size={12} style={{display:'inline',marginRight:4}}/> {t('nav.expert')}</> : <><User size={12} style={{display:'inline',marginRight:4}}/> {t('nav.client')}</>}
                </span>
                <span className="profile-joined">
                  {t('profile.joined')} {new Date(profile.created_at).toLocaleDateString()}
                </span>
                {profile.social_provider && (
                  <span className="profile-social">
                    {t(profile.social_provider === 'google' ? 'profile.google' : 'profile.kakao')} {t('profile.linked')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {isOwnProfile && (
            <div className="profile-actions">
              {editMode ? (
                <>
                  <button onClick={handleSave} disabled={updating} className="btn-save">
                    <Save size={14} style={{marginRight:5,display:'inline',verticalAlign:'middle'}}/>{updating ? t('profile.saving') : t('profile.save')}
                  </button>
                  <button onClick={handleCancel} className="btn-cancel">
                    <X size={14} style={{marginRight:5,display:'inline',verticalAlign:'middle'}}/>{t('common.cancel')}
                  </button>
                </>
              ) : (
                <>
                  <button onClick={handleEdit} className="btn-edit">
                    <Edit2 size={14} style={{marginRight:5,display:'inline',verticalAlign:'middle'}}/>{t('profile.edit')}
                  </button>
                  <button onClick={() => setShowPasswordChange(!showPasswordChange)} className="btn-password">
                    <Lock size={14} style={{marginRight:5,display:'inline',verticalAlign:'middle'}}/>{t('profile.changePw')}
                  </button>
                  <button onClick={handleLogout} className="btn-logout">
                    <LogOut size={14} style={{marginRight:5,display:'inline',verticalAlign:'middle'}}/>{t('nav.logout')}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* 신뢰도 점수 섹션 */}
        <div className="trust-score-section">
          <h2><Target size={20} style={{display:'inline',marginRight:8,verticalAlign:'middle'}}/>{t('profile.trustScore')}</h2>
          <TrustScoreDisplay 
            userId={targetUserId!} 
            showDetails={isOwnProfile || false} 
          />
        </div>

        {/* 상세 프로필 정보 */}
        <div className="profile-details">
          
          {/* 기본 정보 */}
          <div className="detail-section">
            <h3><ClipboardList size={16} style={{display:'inline',marginRight:7,verticalAlign:'middle'}}/>{t('profile.basicInfo')}</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">{t('profile.email')}</span>
                <span className="detail-value">{profile.email}</span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">{t('profile.phone')}</span>
                {editMode ? (
                  <input
                    type="tel"
                    value={editData.phone}
                    onChange={(e) => setEditData({...editData, phone: e.target.value})}
                    placeholder={t('profile.phone')}
                    className="edit-input"
                  />
                ) : (
                  <span className="detail-value">{profile.phone || t('profile.notEntered')}</span>
                )}
              </div>

              <div className="detail-item">
                <span className="detail-label">{t('profile.birthDate')}</span>
                {editMode ? (
                  <input
                    type="date"
                    value={editData.birth_date}
                    onChange={(e) => setEditData({...editData, birth_date: e.target.value})}
                    className="edit-input"
                  />
                ) : (
                  <span className="detail-value">
                    {profile.birth_date ? new Date(profile.birth_date).toLocaleDateString() : t('profile.notEntered')}
                  </span>
                )}
              </div>

              <div className="detail-item">
                <span className="detail-label">{t('profile.gender')}</span>
                {editMode ? (
                  <select
                    value={editData.gender}
                    onChange={(e) => setEditData({...editData, gender: e.target.value})}
                    className="edit-select"
                  >
                    <option value="">{t('profile.selectPlease')}</option>
                    <option value="male">{t('register.male')}</option>
                    <option value="female">{t('register.female')}</option>
                    <option value="other">{t('register.other')}</option>
                  </select>
                ) : (
                  <span className="detail-value">
                    {profile.gender === 'male' ? t('register.male') : 
                     profile.gender === 'female' ? t('register.female') : 
                     profile.gender === 'other' ? t('register.other') : t('profile.notEntered')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* 소개 */}
          <div className="detail-section">
            <h3><MessageSquare size={16} style={{display:'inline',marginRight:7,verticalAlign:'middle'}}/>{t('profile.bio')}</h3>
            {editMode ? (
              <textarea
                value={editData.bio}
                onChange={(e) => setEditData({...editData, bio: e.target.value})}
                placeholder={t('profile.bioPlaceholder')}
                rows={4}
                className="edit-textarea"
              />
            ) : (
              <p className="profile-bio">
                {profile.bio || t('profile.noBio')}
              </p>
            )}
          </div>

          {/* 포트폴리오 */}
          {profile.role === 'freelancer' && (
            <div className="detail-section">
              <h3><Briefcase size={16} style={{display:'inline',marginRight:7,verticalAlign:'middle'}}/>{t('profile.portfolio')}</h3>
              {editMode ? (
                <input
                  type="url"
                  value={editData.portfolio_url}
                  onChange={(e) => setEditData({...editData, portfolio_url: e.target.value})}
                  placeholder={t('profile.portfolioUrl')}
                  className="edit-input"
                />
              ) : (
                <div className="portfolio-link">
                  {profile.portfolio_url ? (
                    <a href={profile.portfolio_url} target="_blank" rel="noopener noreferrer">
                      <Link2 size={14} style={{display:'inline',marginRight:5,verticalAlign:'middle'}}/>{t('profile.viewPortfolio')}
                    </a>
                  ) : (
                    <span className="no-portfolio">{t('profile.noPortfolio')}</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 스킬 */}
          {profile.role === 'freelancer' && (
            <div className="detail-section">
              <h3><Wrench size={16} style={{display:'inline',marginRight:7,verticalAlign:'middle'}}/>{t('profile.skills')}</h3>
              {editMode ? (
                <div className="skills-editor">
                  <div className="skills-input-section">
                    <input
                      type="text"
                      placeholder={t('profile.skillPlaceholder')}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          const input = e.target as HTMLInputElement;
                          addSkill(input.value);
                          input.value = '';
                        }
                      }}
                      className="edit-input"
                    />
                  </div>
                  <div className="skills-list">
                    {editData.skills.map((skill, index) => (
                      <span key={index} className="skill-tag">
                        {skill}
                        <button 
                          onClick={() => removeSkill(skill)}
                          className="skill-remove"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="skills-display">
                  {profile.skills && JSON.parse(profile.skills).length > 0 ? (
                    JSON.parse(profile.skills).map((skill: string, index: number) => (
                      <span key={index} className="skill-tag">{skill}</span>
                    ))
                  ) : (
                    <span className="no-skills">{t('profile.noSkills')}</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 받은 리뷰 섹션 */}
        <div className="detail-section" style={{ marginTop: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 16, letterSpacing: '-0.3px', display:'flex', alignItems:'center', gap:7 }}>
            <Star size={16} />{t('profile.receivedReviews')}
          </h3>
          <ReviewList userId={targetUserId} />
        </div>

        {/* 비밀번호 변경 섹션 */}
        {isOwnProfile && showPasswordChange && (
          <div className="password-change-section">
            <h3><Lock size={16} style={{display:'inline',marginRight:7,verticalAlign:'middle'}}/>{t('profile.changePw')}</h3>
            <div className="password-form">
              <input
                type="password"
                placeholder={t('profile.currentPw')}
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                className="edit-input"
              />
              <input
                type="password"
                placeholder={t('profile.newPw')}
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                className="edit-input"
              />
              <input
                type="password"
                placeholder={t('profile.confirmPw')}
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                className="edit-input"
              />
              <div className="password-actions">
                <button 
                  onClick={handlePasswordChange} 
                  disabled={updating}
                  className="btn-save"
                >
                  {updating ? t('profile.changing') : t('profile.changePw')}
                </button>
                <button 
                  onClick={() => setShowPasswordChange(false)}
                  className="btn-cancel"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 계정 관리 */}
        {isOwnProfile && (
          <div className="account-management">
            <h3><Settings size={16} style={{display:'inline',marginRight:7,verticalAlign:'middle'}}/>{t('profile.accountMgmt')}</h3>
            <div className="danger-zone">
              <button onClick={handleDeleteAccount} className="btn-danger">
                <Trash2 size={14} style={{display:'inline',marginRight:5,verticalAlign:'middle'}}/>{t('profile.deleteAccount')}
              </button>
              <p className="danger-warning">{t('profile.deleteWarning')}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedProfilePage;
